/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  emailLoginConfigs,
  fidpGitHub,
  fidpGoogle,
  fidpMicrosoft,
} from 'config';
import { getGrowthHackingParams } from 'utils/growthHacking/utm';
import logger from 'utils/logger';
import generateUUID from 'utils/uuid';
import { useAuthContext } from '@asgardeo/auth-react';

interface UseSignUpFlowArgs {
  /** Where to land after signing up; goes into the OAuth `state`. */
  returnToUrl: string;
  returnToOrg: string;
  queryParams: URLSearchParams;
}

/**
 * The sign up handlers shared by the signup page and the auth dialog.
 *
 * Sign up differs from sign in in more than the wording: the email path leaves
 * for Asgardeo's own registration page with a post-register callback rather than
 * going through `signIn`, and UTM parameters are parked in local storage on the
 * way out.
 */
export default function useSignUpFlow(args: UseSignUpFlowArgs) {
  const { returnToUrl, returnToOrg, queryParams } = args;

  const history = useHistory();
  const { signIn } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);

  const handleSignUp = async (
    fidp: string,
    stateParam: { [key: string]: string },
    captchaInfo?: string,
    username?: string
  ) => {
    let encodedStateParam = '';
    const modifiedStateParams = { ...stateParam };
    const growthHackingParams = getGrowthHackingParams([...queryParams]);
    const hasGrowthHackingParams = growthHackingParams.length > 0;
    if (hasGrowthHackingParams) {
      growthHackingParams.forEach(([key, value]) => {
        modifiedStateParams[key] = value;
      });
    }
    encodedStateParam = btoa(JSON.stringify(modifiedStateParams));

    try {
      setIsLoading(true);
      const tokenRequestConfig = {
        params: {
          tokenBindingId: generateUUID(),
        },
      };
      signIn(
        {
          fidp,
          state: encodedStateParam,
          ...(username && { username }),
          ...(captchaInfo && { captchaInfo }),
        },
        undefined,
        undefined,
        undefined,
        undefined,
        tokenRequestConfig
      );
    } catch (signInError) {
      logger.error(signInError);
      history.push('/internal-error');
      setIsLoading(false);
    }
  };

  const storeUtmParamsIfNeeded = () => {
    const growthHackingParams = getGrowthHackingParams([...queryParams]);
    if (growthHackingParams.length > 0) {
      localStorage.setItem('utm-params', returnToUrl);
    }
  };

  const handleEmailSignup = () => {
    storeUtmParamsIfNeeded();
    const callbackUrl = new URL(emailLoginConfigs.POST_REGISTER_CALLBACK_URL);
    callbackUrl.searchParams.set('returnToUrl', returnToUrl);
    const emailSignupUrl =
      `${emailLoginConfigs.ASGARDEO_SIGNUP_URL}?initiated-platform=devant&template-name=` +
      `${emailLoginConfigs.REGISTER_MAIL_TEMPLATE_NAME}&tenant-url-prefix=${emailLoginConfigs.TENANT_URL_PREFIX}` +
      `&post-register-callback=${encodeURIComponent(callbackUrl.toString())}`;
    window.location.href = emailSignupUrl;
  };

  const handleGithubSignup = () => {
    const stateParamObj = {
      fidpId: fidpGitHub,
      returnToUrl,
      returnToOrg,
    };
    handleSignUp(fidpGitHub, stateParamObj);
  };

  const handleGoogleSignup = () => {
    const stateParamObj = {
      fidpId: fidpGoogle,
      returnToUrl,
      returnToOrg,
    };
    handleSignUp(fidpGoogle, stateParamObj);
  };

  const handleMicrosoftSignup = () => {
    const stateParamObj = {
      fidpId: fidpMicrosoft,
      returnToUrl,
      returnToOrg,
    };
    handleSignUp(fidpMicrosoft, stateParamObj);
  };

  return {
    isLoading,
    handleSignUp,
    handleEmailSignup,
    handleGithubSignup,
    handleGoogleSignup,
    handleMicrosoftSignup,
  };
}
